import { FilterExpression, like } from '@infuse/api/common';

import {
  createChargingStationType,
  deleteChargingStationType,
  searchChargingStationTypes,
  updateChargingStationType
} from '@infuse/api/cpms/config';
import { defineStore } from 'pinia';
import { apiResultToDomain, mapDomainToChargingStationType } from './conversion';

import { ChargingStationType } from './model';

interface State {
  items: ChargingStationType[];
  options: {
    query?: string;
    filter?: FilterExpression;
    page: number;
    lastPage: boolean;
  };
  loading: boolean;
}

export const useChargingStationTypeStore = defineStore('charging-station-type', {
  state: () => {
    return {
      items: [],
      options: {
        query: '',
        filter: undefined,
        page: 0,
        lastPage: false
      },
      loading: false
    } as State;
  },
  getters: {},
  actions: {
    async search() {
      try {
        this.loading = true;

        const apiResponse = await searchChargingStationTypes({
          sort: [{ key: 'manufacturer.code' }, { key: 'code' }],
          size: 20,
          filter: this.options.filter,
          page: this.options.page
        });

        this.options.lastPage = apiResponse.last;

        const responseItems = apiResponse.content ? apiResponse.content.map(apiResultToDomain) : [];

        if (apiResponse.first) {
          this.items = responseItems;
        } else {
          this.items = [...this.items, ...responseItems];
        }
      } finally {
        this.loading = false;
      }
    },

    async setSearchQuery(searchQuery: string) {
      this.resetPageOptions();
      this.options.query = `%${searchQuery.toLowerCase().replace('%', '\\%')}%`;
      this.options.filter = like('lower(code)', this.options.query).or(
        like('lower(manufacturer.code)', this.options.query)
      );

      await this.search();
    },

    async create(chargingStationType: ChargingStationType) {
      try {
        this.loading = true;

        await createChargingStationType({}, mapDomainToChargingStationType(chargingStationType));
        await this.search();
      } finally {
        this.loading = false;
      }
    },

    async update(chargingStationType: ChargingStationType) {
      try {
        this.loading = true;

        await updateChargingStationType(
          { id: chargingStationType.id },
          mapDomainToChargingStationType(chargingStationType)
        );
        await this.search();
      } finally {
        this.loading = false;
      }
    },

    async delete(chargingStationType: ChargingStationType) {
      try {
        this.loading = true;

        await deleteChargingStationType({ id: chargingStationType.id });
        await this.search();
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      if (!this.options.lastPage) {
        this.options.page++;
        await this.search();
      }
    },

    async getAllTypes() {
      //There is this annoying issue where I want to get all unique types / manufacturers combinations from the backend.
      //Receiving a page of 20 items is not enough to get all of them, and loading more in a paginated fashion in a dropdown is not supported and might cause more issues.
      //Like having a valid type / vendor combination but one of the items is not in the pagination results.
      this.loading = true;

      const apiResponse = await searchChargingStationTypes({
        sort: [{ key: 'manufacturer.code' }, { key: 'code' }],
        size: 1000,
        page: 0
      });
      const responseItems = apiResponse.content ? apiResponse.content.map(apiResultToDomain) : [];

      if (apiResponse.first) {
        this.items = responseItems;
      } else {
        this.items = [...this.items, ...responseItems];
      }

      this.loading = false;
    },

    resetPageOptions() {
      this.options.page = 0;
      this.options.lastPage = false;
    },

    resetOptions() {
      this.options.query = '';
      this.options.filter = undefined;
      this.resetPageOptions();
    }
  }
});
